import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, FileSpreadsheet, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { ROOM_KIND_LABEL } from '../lib/format';
import { invalidateInventory } from '../lib/hooks';
import type { RoomKind } from '../lib/types';
import { Alert, Badge, Button, Modal, Table, Td, Th } from './ui';

interface PlannedRoom {
  name: string;
  roomNumber: string | null;
  kind: RoomKind;
  capacity: number | null;
  action: 'create' | 'update' | 'unchanged';
}

interface ImportResult {
  buildingsCreated: number;
  roomsCreated: number;
  roomsUpdated: number;
  renamed: number;
  unchanged: number;
  skipped: number;
  errors: { row: number; message: string }[];
  plan: { building: string; isNew: boolean; rooms: PlannedRoom[] }[];
  committed: boolean;
}

const ACTION_TONE = { create: 'green', update: 'blue', unchanged: undefined } as const;
const ACTION_LABEL = { create: 'New', update: 'Filled in', unchanged: 'Already here' };

/**
 * Buildings and rooms from Blackbaud's own export. Blackbaud has no API for these, so a school exports the file by
 * hand and uploads it here: Edvora reads it, shows what it would do, and keeps nothing but the buildings and rooms.
 */
export function ImportBuildingsModal({ open, onClose }: { open: boolean; onClose(): void }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [done, setDone] = useState<ImportResult | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setFile(null);
      setPreview(null);
      setDone(null);
      setError('');
    }
  }, [open]);

  const run = useMutation({
    mutationFn: async (commit: boolean) => api.post<ImportResult>('/buildings/import', { csv: await file!.text(), commit }),
    onSuccess: (result) => {
      setError('');
      if (result.committed) {
        setDone(result);
        invalidateInventory(queryClient);
        void queryClient.invalidateQueries({ queryKey: ['buildings'] });
      } else {
        setPreview(result);
      }
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const result = done ?? preview;
  const nothingToDo = !!preview && preview.buildingsCreated === 0 && preview.roomsCreated === 0 && preview.roomsUpdated === 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Import buildings and rooms from Blackbaud"
      description="In Blackbaud, go to Core › Settings › School information › Buildings and choose Export. Upload that file here - it isn’t stored."
      footer={
        done ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            {preview ? (
              <Button variant="primary" icon={<Upload />} loading={run.isPending} disabled={nothingToDo} onClick={() => run.mutate(true)}>
                {nothingToDo ? 'Nothing to import' : `Import ${preview.buildingsCreated + preview.roomsCreated + preview.roomsUpdated} change${preview.buildingsCreated + preview.roomsCreated + preview.roomsUpdated === 1 ? '' : 's'}`}
              </Button>
            ) : (
              <Button variant="primary" icon={<FileSpreadsheet />} disabled={!file} loading={run.isPending} onClick={() => run.mutate(false)}>
                Check the file
              </Button>
            )}
          </>
        )
      }
    >
      <div className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}

        {!result && (
          <>
            <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 px-6 py-10 text-center hover:border-brand-400 hover:bg-brand-50/30">
              <Building2 className="size-8 text-slate-400" />
              <span className="mt-2 text-sm font-medium text-slate-800">{file ? file.name : 'Choose the Buildings export'}</span>
              <span className="mt-1 text-xs text-slate-500">A row per room, with its building, number, capacity and type. Nothing is written until you say so.</span>
              <input type="file" accept=".csv,text/csv" className="sr-only" onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
            </label>
            <p className="text-sm text-slate-600">
              Buildings and rooms that aren’t here yet are created. A room you already have keeps its name and type -
              only its number, capacity and notes are filled in when they’re empty. Nothing is renamed or deleted, so
              uploading the same file twice changes nothing.
            </p>
          </>
        )}

        {result && (
          <>
            <div className="grid grid-cols-2 gap-3 text-center sm:grid-cols-4">
              {[
                ['Buildings', result.buildingsCreated, 'text-emerald-700'],
                ['Rooms', result.roomsCreated, 'text-emerald-700'],
                ['Filled in', result.roomsUpdated, 'text-sky-700'],
                ['Already here', result.unchanged, 'text-slate-700'],
              ].map(([label, value, color]) => (
                <div key={label as string} className="rounded-lg border border-slate-200 p-3">
                  <p className={`text-2xl font-semibold ${color as string}`}>{value as number}</p>
                  <p className="text-xs text-slate-500">{label as string}</p>
                </div>
              ))}
            </div>

            {done && <Alert tone="green" title="Imported">Your buildings and rooms are up to date with that file.</Alert>}
            {result.renamed > 0 && (
              <Alert tone="blue">
                {result.renamed} room{result.renamed === 1 ? '' : 's'} shared a name with another room in the same building, so the room number was added to
                tell them apart.
              </Alert>
            )}
            {result.skipped > 0 && (
              <p className="text-sm text-slate-500">
                {result.skipped} row{result.skipped === 1 ? '' : 's'} skipped: repeats of a room already in the file, or a building marked inactive.
              </p>
            )}

            {result.errors.length > 0 && (
              <div className="rounded-lg border border-slate-200">
                <Table>
                  <thead>
                    <tr>
                      <Th>Row</Th>
                      <Th>Problem</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {result.errors.map((problem) => (
                      <tr key={problem.row}>
                        <Td className="w-16 font-mono text-xs">{problem.row}</Td>
                        <Td className="text-red-700">{problem.message}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {!done && (
              <div className="max-h-80 space-y-4 overflow-y-auto rounded-lg border border-slate-200 p-4">
                {result.plan.map((group) => (
                  <section key={group.building}>
                    <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
                      {group.building}
                      {group.isNew ? <Badge tone="green">New building</Badge> : <Badge>Already here</Badge>}
                      <span className="text-xs font-normal text-slate-500">
                        {group.rooms.length} room{group.rooms.length === 1 ? '' : 's'}
                      </span>
                    </h3>
                    <ul className="mt-1 grid gap-x-4 gap-y-1 sm:grid-cols-2">
                      {group.rooms.map((room) => (
                        <li key={room.name} className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
                          <span className="font-medium">{room.name}</span>
                          <span className="text-xs text-slate-500">
                            {ROOM_KIND_LABEL[room.kind]}
                            {room.capacity ? ` · seats ${room.capacity}` : ''}
                          </span>
                          <Badge tone={ACTION_TONE[room.action]}>{ACTION_LABEL[room.action]}</Badge>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
